import ErrorHandler from '../utils/ErrorHandler';
import { supabase } from './client';
import { getCurrentOrgID, getOrgTimezone } from '../data/organization';
import { toReportDay, reportDayStartISO, reportDayEndISO } from '../utils/reportDates';
import DataExporter from '@/utils/DataExporter';
import useLoader from '@/utils/hooks/useLoader';


const getMissedReport = async ({days, filters = {}, eventID = null, searchText}) => {
    const orgID = await getCurrentOrgID();
    const {data, error} = await supabase.rpc("analytics_missed_report", {days, eventid: eventID, filters: {...filters , search: searchText.trim() || undefined}});

    if(error){
        ErrorHandler(Error(error.message), {query: "getMissedReport", orgID, error}, "There was an error fetching the missed report. Please try again later.")
        throw Error;
    }

    return data || []
}


const getBirthdayReport = async ({days, filters = {}, searchText}) => {
    const orgID = await getCurrentOrgID();
    const {data, error} = await supabase.rpc('analytics_get_birthdays', {
        days, 
        filters: {...filters, search: searchText?.trim() || undefined}
    });

    if(error){
        ErrorHandler(Error(error.message), {query: "getBirthdayReport", orgID, error}, "There was an error fetching the birthday report. Please try again later.")
        throw Error;
    }

    return data || []
}

const getAttendanceUniqueKPIs = async (startDate, endDate) => {
    const orgID = await getCurrentOrgID();
    const {data: uniqueAttendees, error: uniqueAttendeesError} = await supabase.rpc('analytics_get_unique_attendees', {start_date: await toReportDay(startDate), end_date: await toReportDay(endDate)});

    if(uniqueAttendeesError){
        const error = uniqueAttendeesError;
        ErrorHandler(Error(error.message), {query: "getAttendanceKPIs", orgID, error}, "There was an error fetching the attendance KPIs. Please try again later.")
        throw error;
    }

    return uniqueAttendees
}

const getAttendanceFirstFouthTimeVisitorsKPIs = async (startDate, endDate) => {
    const orgID = await getCurrentOrgID();
    const orgTz = await getOrgTimezone();
    const {data, error} = await supabase.rpc('analytics_get_first_fouth_time_visitors', {start_date: reportDayStartISO(await toReportDay(startDate), orgTz), end_date: reportDayEndISO(await toReportDay(endDate), orgTz)});

    if(error){
        ErrorHandler(Error(error.message), {query: "getAttendanceFirstFouthTimeVisitorsKPIs", orgID, error}, "There was an error fetching the attendance first/fouth time visitors KPIs. Please try again later.")
        throw Error;
    }

    return data
}

const getAttendanceUniqueDetail = async (startDate, endDate) => {
    const {data, error} = await supabase.rpc('analytics_get_unique_attendees_detail', {start_date: await toReportDay(startDate), end_date: await toReportDay(endDate)});
    if (error) {
        ErrorHandler(Error(error.message), {query: "getAttendanceUniqueDetail", error});
        throw Error;
    }
    return data || [];
}

const getAttendanceFirstFouthTimeVisitorsDetail = async (startDate, endDate) => {
    const orgTz = await getOrgTimezone();
    const {data, error} = await supabase.rpc('analytics_get_first_fouth_time_visitors_detail', {start_date: reportDayStartISO(await toReportDay(startDate), orgTz), end_date: reportDayEndISO(await toReportDay(endDate), orgTz)});
    if (error) {
        ErrorHandler(Error(error.message), {query: "getAttendanceFirstFouthTimeVisitorsDetail", error});
        throw Error;
    }
    return data || [];
}

const getAttendanceWeeklyUniqueAvgKPIs = async (startDate, endDate) => {
    const orgID = await getCurrentOrgID();
    const {data, error} = await supabase.rpc('analytics_get_weekly_unique_avg', {start_date: await toReportDay(startDate), end_date: await toReportDay(endDate)});

    if (error) {
        ErrorHandler(Error(error.message), {query: "getAttendanceWeeklyUniqueAvgKPIs", orgID, error}, "There was an error fetching the weekly unique average. Please try again later.")
        throw error;
    }

    return data
}

const getAttendanceDateFieldsKPIs = async (startDate, endDate, dateFieldID, countOnly = true) => {
    const orgID = await getCurrentOrgID();

    const {data: dateFieldValues, error: dateFieldValuesError} = await supabase.rpc('analytics_get_people_by_custom_date_field', {start_date: await toReportDay(startDate), end_date: await toReportDay(endDate), field_id: dateFieldID, count_only: countOnly});
    
    if(!dateFieldValuesError){
        if(countOnly){
            return {id: dateFieldID, count: dateFieldValues[0].record_count};
        }
        else{
            return {id: dateFieldID, values: dateFieldValues};
        }
    }
    else{
        ErrorHandler(Error(dateFieldValuesError.message), {query: "getAttendanceDateFieldsKPIs", orgID, error: dateFieldValuesError})
        throw Error
    }
}

const getVolunteerUtilization = async ({startDate, endDate, dueTypes}) => {
    const {data, error} = await supabase.rpc('analytics_volunteer_utilization', {
        p_start_date: await toReportDay(startDate),
        p_end_date: await toReportDay(endDate),
        p_due_types: dueTypes?.length ? dueTypes : null
    });

    if (error) {
        ErrorHandler(Error(error.message), {query: 'getVolunteerUtilization', input: {startDate, endDate, dueTypes}, error}, 'There was an error fetching volunteer utilization. Please try again later.');
        throw error;
    }

    return data || [];
};

const exportMissedReport = async ({days, eventID = null, filters = {}, searchText = '', selectedFields = {}}) => {
    useLoader.getState().enable();

    try {
        const {data, error} = await supabase.rpc("analytics_missed_report", {days, eventid: eventID, filters: {...filters, search: searchText.trim() || undefined}});

        if(error){
            ErrorHandler(Error(error.message), {query: "exportMissedReport", error}, "There was an error exporting the missed report.")
            return;
        }

        const { basicFields = [], customFields = [] } = selectedFields;

        const exportData = (data || []).map(row => {
            const item = row.stats;
            const rowData = {};

            if (basicFields.includes('id')) rowData.id = item.id;
            if (basicFields.includes('firstName')) rowData.firstName = item.firstName;
            if (basicFields.includes('lastName')) rowData.lastName = item.lastName;
            if (basicFields.includes('dateOfBirth')) rowData.dateOfBirth = item.dateOfBirth;
            if (basicFields.includes('age')) rowData.age = item.age;
            if (basicFields.includes('gender')) rowData.gender = item.gender;
            if (basicFields.includes('email')) rowData.email = item.email;
            if (basicFields.includes('phone')) rowData.phone = (item.Phones || []).map(p => p.phoneNumber).join(", ");
            const addr = item.Address;
            if (basicFields.includes('address') || basicFields.includes('street')) rowData.street = addr?.street || '';
            if (basicFields.includes('address') || basicFields.includes('streetLine2')) rowData.streetLine2 = addr?.streetLine2 || '';
            if (basicFields.includes('address') || basicFields.includes('city')) rowData.city = addr?.city || '';
            if (basicFields.includes('address') || basicFields.includes('state')) rowData.state = addr?.state || '';
            if (basicFields.includes('address') || basicFields.includes('zip')) rowData.zip = addr?.zip || '';
            if (basicFields.includes('lastVisit')) rowData.lastVisit = item.lastVisit;

            customFields.forEach(cf => {
                rowData[cf.name] = item.customFieldValues?.[cf.id] || "";
            });

            return rowData;
        });

        DataExporter.exportToCSV(exportData, "missed_report");
    } catch (error) {
        ErrorHandler(Error(error.message), {query: "exportMissedReport", days, eventID, filters}, "Something went wrong exporting the missed report")
    } finally {
        useLoader.getState().disable();
    }
}

export {
    getMissedReport,
    exportMissedReport,
    getBirthdayReport,
    getAttendanceUniqueKPIs,
    getAttendanceUniqueDetail,
    getAttendanceFirstFouthTimeVisitorsKPIs,
    getAttendanceFirstFouthTimeVisitorsDetail,
    getAttendanceDateFieldsKPIs,
    getAttendanceWeeklyUniqueAvgKPIs,
    getVolunteerUtilization
}